import { useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';

import type { EditTextHandle } from '@/features/storefront/sections/SectionRenderer';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n';
import { sanitizeRichText } from '@/lib/richText';

/**
 * A paragraph of formatted writing - a text section's body - typed into on the
 * page.
 *
 * The plain-text `EditableText` cannot do this: the body is HTML, with its
 * paragraphs, bold and links, and editing it as bare text would flatten all of
 * that the first time a merchant fixed a typo. So this box edits the markup
 * itself, with the browser's own rich editing - Enter starts a new paragraph,
 * Ctrl+B and Ctrl+I work as they do everywhere - and what comes out goes
 * through the same `sanitizeRichText` the side panel's editor and the
 * storefront use, so nothing typed or pasted here can put more on the page
 * than the panel could.
 *
 * Like `EditableText`: absent without a handle (the live shop and every
 * unselected section render the plain `<div>`), swapped for a separate
 * element while editing so React never reconciles into what the browser is
 * rewriting, written once when the merchant clicks away - one undo step - and
 * put back as it was on Esc. Enter does not finish here: a paragraph needs it.
 */
export function EditableRichText({
  handle,
  html,
  className,
}: {
  handle: EditTextHandle | null;
  /** The body as stored; sanitised before it is shown or edited. */
  html: string;
  className?: string;
}) {
  const clean = sanitizeRichText(html);
  if (!handle || !handle.editable) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: clean }} />;
  }
  return <RichSurface handle={handle} html={clean} className={className} />;
}

function RichSurface({
  handle,
  html,
  className,
}: {
  handle: EditTextHandle;
  html: string;
  className?: string;
}) {
  const t = useT();
  const [editing, setEditingState] = useState(false);
  // Readable synchronously, so the blur an unmount fires cannot commit twice.
  const open = useRef(false);
  const setEditing = (next: boolean) => {
    open.current = next;
    setEditingState(next);
  };
  const box = useRef<HTMLDivElement>(null);
  const cancelled = useRef(false);
  const opening = useRef(html);
  opening.current = html;

  const label = t('sectionEditor.inline.on_page', { field: t('sectionEditor.inline.field.body') });
  const hint = t('sectionEditor.inline.hint_rich');

  useLayoutEffect(() => {
    if (!editing) return;
    const element = box.current;
    if (!element) return;
    element.innerHTML = opening.current;
    element.focus();
    const selection = window.getSelection?.();
    if (selection && typeof document.createRange === 'function') {
      const range = document.createRange();
      range.selectNodeContents(element);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }, [editing]);

  function finish() {
    if (!open.current) return;
    setEditing(false);
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    const element = box.current;
    if (!element) return;
    const next = sanitizeRichText(element.innerHTML).trim();
    if (next !== html.trim()) handle.onCommit(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // The editor's own shortcuts must not fire while the merchant is writing.
    event.stopPropagation();
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelled.current = true;
      finish();
    }
  }

  const frame =
    'cursor-text rounded-sm outline-offset-4 hover:outline hover:outline-1 hover:outline-dashed focus:outline focus:outline-2';

  if (editing) {
    return (
      <div
        key="editing"
        ref={box}
        role="textbox"
        aria-label={label}
        aria-multiline
        tabIndex={0}
        contentEditable
        suppressContentEditableWarning
        spellCheck
        onBlur={finish}
        onKeyDown={onKeyDown}
        className={cn(className, frame, 'min-h-[1.5em]')}
        data-inline-edit="editing"
      />
    );
  }

  return (
    <div
      key="rest"
      role="textbox"
      aria-label={label}
      aria-multiline
      tabIndex={0}
      title={hint}
      onFocus={() => setEditing(true)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.stopPropagation();
          setEditing(true);
        }
      }}
      className={cn(className, frame)}
      data-inline-edit="rest"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
